<template>
  <div class="c-partner-data-sharing">
    <div>
      <h3 class="text-neutral-text-strong text-lg font-bold mb-4">
        Partner Populations
      </h3>
      <p class="mb-16">
        View and request access to your partner's populations.
      </p>
      <PopulationTabActions
        class="mb-16"
        ref="populationTabActionsRef"
        :event-site="EVENT_SITES.PARTNER_DATA_SHARING_TAB"
        :first-filter="populationTypeFilter"
        :initial-filter-keys="['population_type', 'sharing_level']"
        :initial-filter-values="initialFilterValues"
        :show-edit-button="false"
        :include-shared-fields-filter="false"
        search-placeholder="Search populations"
        @search-applied="handleSearchApplied"
        @filters-applied="handleFiltersApplied"
      />
    </div>
    <BittsEmptyState
      v-if="filteredPartnerPopulations.length === 0"
      title="No Results"
      description="Try different filters or search term to find your population"
      :button="{
        text: 'Clear Filters',
        size: 'small',
        variant: 'outline',
        type: 'primary',
      }"
      font-awesome-icon-class="c-partner-data-sharing__empty-state-icon"
      :font-awesome-icon="['fak', 'search']"
      :font-awesome-icon-style="{ height: '52px', width: '52px' }"
      @button-clicked="handleClearFilters"
    />
    <div v-else>
      <ListContainer
        :list="filteredPartnerPopulations"
        :get-key="
          (pop) => (pop.isEmpty ? `empty-${pop.standard_type}` : pop.id)
        "
        :has-divider="() => false"
        :show-hover="true"
      >
        <template #header>
          <div
            class="px-12 py-4 grid grid-cols-[6fr_3fr_3fr_2fr] gap-4 border-b border-neutral-border"
          >
            <div
              v-for="header in headers"
              :key="header.label"
              class="flex items-center gap-4"
            >
              <p class="text-sm font-bold text-neutral-text">
                {{ header.label }}
              </p>
              <component :is="header.tooltip ? BittsTooltip : 'div'">
                <FontAwesomeIcon
                  v-if="header.tooltip"
                  :icon="['fak', 'information-circle']"
                  :style="{
                    height: '12px',
                    width: '12px',
                    color: 'currentColor',
                  }"
                  class="text-neutral-accent"
                />
                <template v-if="header.tooltip" #title>
                  {{ header.tooltip }}
                </template>
              </component>
            </div>
          </div>
        </template>
        <template #default="{ item }">
          <div class="p-16 grid grid-cols-[6fr_3fr_3fr_2fr] gap-4">
            <div class="flex items-center gap-10 min-w-0">
              <component
                :is="item.description ? BittsTooltip : 'div'"
                class="min-w-0"
              >
                <div class="flex items-center gap-8 min-w-0">
                  <BittsAvatar
                    v-if="item.integration_type"
                    size="small"
                    shape="square"
                    :show-border="true"
                    :custom-svg="
                      item.integration_type
                        ? `${item.integration_type}Icon`
                        : ''
                    "
                    class="c-partner-data-sharing__integration-avatar"
                  />
                  <p
                    :class="{ 'text-neutral-text': item.isEmpty }"
                    class="font-bold truncate min-w-0"
                  >
                    {{ item.name }}
                  </p>
                </div>
                <template #title>
                  {{ item.description }}
                </template>
              </component>
              <BittsTag
                v-if="item.standard_type"
                color="info"
                size="x-small"
                text="Standard"
              />
            </div>
            <div class="min-w-0">
              <SharingSettingsVisibilitySelector
                v-if="!item.isEmpty"
                :data-share="getDataShareForPopulation(item.id)"
                :is-own="false"
                read-only
                display="visibility"
              />
            </div>
            <div class="min-w-0">
              <SharingSettingsVisibilitySelector
                v-if="!item.isEmpty"
                :data-share="getDataShareForPopulation(item.id)"
                :is-own="false"
                read-only
                display="fields"
                placement="right"
              />
            </div>
            <ShareRequestActions
              v-if="item.requestDataIsVisible && hasManageSharing"
              class="ml-auto"
              :data-request-information="item.dataRequestInformation"
              :has-data-sharing-hub="true"
              @data-requested="emit('open-data-share-request-modal', item)"
            />
          </div>
        </template>
      </ListContainer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  BittsAvatar,
  BittsEmptyState,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { computed, ref } from 'vue';

import ListContainer from '@/components/ListContainer.vue';
import ShareRequestActions from '@/components/partners/ShareRequestActions.vue';
import PopulationTabActions from '@/components/sharing-settings/population-tab/PopulationTabActions.vue';
import SharingSettingsVisibilitySelector from '@/components/sharing-settings/SharingSettingsVisibilitySelector.vue';

import useAuth from '@/composables/useAuth';
import { type SharingType } from '@/constants/data_shares';
import { useDataSharesStore } from '@/stores';
import type { PartnerPopulationWithMetadata } from '@/types/data_share_requests';
import {
  ALL_SEGMENTS,
  SEGMENTS_TO_LABEL_MAP,
  type SegmentType,
} from '@/types/populations';

type FilterOption = {
  label: string;
  value: string | number;
};

type FilterValues = Record<string, (string | number)[]>;

type PartnerPopulationWithEmpty = PartnerPopulationWithMetadata & {
  isEmpty?: boolean;
};

const props = defineProps<{
  partnerPopulationsWithMetadata: PartnerPopulationWithEmpty[];
  initialSegment?: SegmentType;
}>();

const emit = defineEmits<{
  'open-data-share-request-modal': [population: PartnerPopulationWithEmpty];
}>();

const { hasPermission } = useAuth();
const dataSharesStore = useDataSharesStore();

const hasManageSharing = computed(() => hasPermission('manage:sharing'));

const searchValue = ref('');

// Filter state
const filterValues = ref<FilterValues>({});
const populationTabActionsRef = ref<InstanceType<
  typeof PopulationTabActions
> | null>(null);

const initialFilterValues = computed<FilterValues | undefined>(() =>
  props.initialSegment
    ? { population_type: [props.initialSegment] }
    : undefined,
);

// Population Type filter options
const populationTypeOptions = computed<FilterOption[]>(() => {
  return ALL_SEGMENTS.flatMap((type) => {
    const label = SEGMENTS_TO_LABEL_MAP[type as SegmentType];
    return label ? [{ label, value: type }] : [];
  });
});

// Define first filter for PopulationTabActions
const populationTypeFilter = computed<{
  value: string;
  label: string;
  faIcon: ['fak', string];
  options: FilterOption[];
}>(() => ({
  value: 'population_type',
  label: 'Population Type',
  faIcon: ['fak', 'population'],
  options: populationTypeOptions.value,
}));

const filteredPartnerPopulations = computed(() => {
  let filtered = props.partnerPopulationsWithMetadata;

  // Apply search filter
  if (searchValue.value) {
    const searchLower = searchValue.value.toLowerCase();
    filtered = filtered.filter((pop) =>
      pop.name.toLowerCase().includes(searchLower),
    );
  }

  // Apply population type filter
  if (
    filterValues.value.population_type &&
    filterValues.value.population_type.length > 0
  ) {
    filtered = filtered.filter((pop) => {
      return (
        pop.segment &&
        (filterValues.value.population_type?.includes(pop.segment) ?? false)
      );
    });
  }

  // Apply sharing level filter
  if (
    filterValues.value.sharing_level &&
    filterValues.value.sharing_level.length > 0
  ) {
    filtered = filtered.filter((pop) => {
      const visibility = pop.dataShare?.visibility;
      return (
        visibility &&
        (filterValues.value.sharing_level?.includes(visibility) ?? false)
      );
    });
  }

  return filtered;
});

function getDataShareForPopulation(partnerPopulationId: number) {
  const populationData = props.partnerPopulationsWithMetadata.find(
    (pop) => pop.id === partnerPopulationId,
  );

  if (!populationData) {
    return undefined;
  }

  const sharingRule = dataSharesStore.getIncomingSharingRules({
    partnerPopulationId,
    isOverride: false,
  })[0];

  const visibility = populationData.dataShare?.visibility as
    | SharingType
    | undefined;

  return {
    visibility,
    sourceFields: populationData.sourceFields || [],
    ...(sharingRule ?? {}),
    population_id: sharingRule?.population_id ?? undefined,
    partner_org_id: sharingRule?.partner_org_id ?? undefined,
    partner_population_id: sharingRule?.partner_population_id ?? undefined,
  };
}

const headers = computed(() => [
  {
    label: 'Population Name',
  },
  {
    label: 'Sharing Level',
    tooltip:
      "How much of this population's data your partner is sharing with you.",
  },
  {
    label: 'Field Presets',
    tooltip: 'Which data fields your partner is sharing with you.',
  },
]);

function handleSearchApplied(value: string) {
  searchValue.value = value;
}

function handleFiltersApplied(newFilterValues: FilterValues) {
  filterValues.value = newFilterValues;
}

function handleClearFilters() {
  filterValues.value = {};
  searchValue.value = '';
  populationTabActionsRef.value?.clearAllFilters();
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.c-partner-data-sharing {
  :deep(.item--first) {
    @apply rounded-tr-none rounded-tl-none;
  }
}

:deep(.c-partner-data-sharing__empty-state-icon) {
  @apply text-neutral-text-weak p-[26px] rounded-[52px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      color-mix(in srgb, theme(colors.neutral-accent) 20%, transparent),
      color-mix(in srgb, theme(colors.neutral-accent) 0%, transparent)
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}
:deep(.c-partner-data-sharing__integration-avatar) {
  span {
    @apply !bg-white;
  }
}
</style>
